import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const COST_CENTERS_QUERY_KEY = ["costCenters"];

// Usar os tipos do arquivo cost-center.ts para manter consistência
export type { CostCenter, CostCenterFormData } from "@/types/cost-center";

// Tipo para linhas da tabela com ações
export interface CostCenterTableRow extends CostCenter {
  renderActions?: () => JSX.Element;
}

// Função para buscar todos os centros de custo do usuário
async function fetchCostCenters(): Promise<CostCenter[]> {
  const { data: user } = await supabase.auth.getUser();
  if (!user || !user.user) {
    throw new Error("Usuário não autenticado");
  }

  const { data, error } = await supabase
    .from("cost_centers")
    .select("*")
    .eq("user_id", user.user.id)
    .order("name", { ascending: true });

  if (error) {
    console.error("Error fetching cost centers:", error);
    throw error;
  }

  return data || [];
}

// Função para buscar um centro de custo pelo ID
async function fetchCostCenterById(id: number): Promise<CostCenter> {
  const { data, error } = await supabase
    .from("cost_centers")
    .select("*")
    .eq("id", id)
    .single();

  if (error) {
    console.error("Error fetching cost center:", error);
    throw error;
  }

  return data;
}

// Função para criar um novo centro de custo
async function createCostCenter(params: { formData: CostCenterFormData; userId: string }): Promise<CostCenter> {
  const { formData, userId } = params;

  // Preparar dados base (sem id - deixar o DEFAULT do banco gerar)
  const insertData: any = {
    name: formData.name,
    description: formData.description || null,
    user_id: userId,
    code: formData.code || null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };

  console.log('📤 Inserindo centro de custo:', insertData);

  const { data, error } = await supabase
    .from("cost_centers")
    .insert(insertData)
    .select()
    .single();

  if (error) {
    console.error("Erro ao criar centro de custo:", error);
    throw error;
  }

  console.log('✅ Centro de custo criado:', data);
  return data;
}

// Função para atualizar um centro de custo existente
async function updateCostCenter(id: number, formData: CostCenterFormData): Promise<CostCenter> {
  // Preparar dados de atualização (a migração foi aplicada)
  const updateData: any = {
    name: formData.name,
    description: formData.description || null,
    code: formData.code || null,
    updated_at: new Date().toISOString(),
  };

  const { data, error } = await supabase
    .from("cost_centers")
    .update(updateData)
    .eq("id", id)
    .select()
    .single();

  if (error) {
    console.error("Error updating cost center:", error);
    throw error;
  }

  return data;
}

// Função para deletar um centro de custo
async function deleteCostCenter(id: number): Promise<void> {
  const { error } = await supabase
    .from("cost_centers")
    .delete()
    .eq("id", id);

  if (error) {
    console.error("Error deleting cost center:", error);
    throw error;
  }
}

// Hook personalizado para gerenciar centros de custo
export function useCostCenters() {
  const queryClient = useQueryClient();

  // Query para buscar todos os centros de custo
  const costCentersQuery = useQuery({
    queryKey: COST_CENTERS_QUERY_KEY,
    queryFn: fetchCostCenters,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Query para buscar um centro de custo específico
  const useCostCenter = (id: number) => {
    return useQuery({
      queryKey: [...COST_CENTERS_QUERY_KEY, id],
      queryFn: () => fetchCostCenterById(id),
      enabled: !!id,
    });
  };

  // Mutation para criar centro de custo
  const createCostCenterMutation = useMutation({
    mutationFn: ({ formData, userId }: { formData: CostCenterFormData; userId: string }) =>
      createCostCenter({ formData, userId }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: COST_CENTERS_QUERY_KEY });
      toast.success("Centro de custo criado com sucesso!");
      return data; // Return the created cost center data
    },
    onError: (error: any) => {
      console.error("Error creating cost center:", error);
      toast.error("Erro ao criar centro de custo. Tente novamente.");
    },
  });

  // Mutation para atualizar centro de custo
  const updateCostCenterMutation = useMutation({
    mutationFn: ({ id, formData }: { id: number; formData: CostCenterFormData }) =>
      updateCostCenter(id, formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COST_CENTERS_QUERY_KEY });
      toast.success("Centro de custo atualizado com sucesso!");
    },
    onError: (error: any) => {
      console.error("Error updating cost center:", error);
      toast.error("Erro ao atualizar centro de custo. Tente novamente.");
    },
  });

  // Mutation para deletar centro de custo
  const deleteCostCenterMutation = useMutation({
    mutationFn: deleteCostCenter,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COST_CENTERS_QUERY_KEY });
      toast.success("Centro de custo deletado com sucesso!");
    },
    onError: (error: any) => {
      console.error("Error deleting cost center:", error);
      toast.error("Erro ao deletar centro de custo. Tente novamente.");
    },
  });

  return {
    // Queries
    costCenters: costCentersQuery.data || [],
    isLoading: costCentersQuery.isLoading,
    error: costCentersQuery.error,
    useCostCenter,

    // Mutations
    createCostCenter: createCostCenterMutation.mutateAsync,
    updateCostCenter: updateCostCenterMutation.mutateAsync,
    deleteCostCenter: deleteCostCenterMutation.mutateAsync,

    // Loading states
    isCreating: createCostCenterMutation.isPending,
    isUpdating: updateCostCenterMutation.isPending,
    isDeleting: deleteCostCenterMutation.isPending,
  };
}
